/** Model-provider spend — the burn-anomaly forecast the provider-burn cron runs every 6 hours. */
import type { IssueDefinition } from "../issue-definition";
import { ISSUE_CATEGORY } from "../issue-enums";

export const PROVIDER_SPEND_ISSUES = {
  /**
   * A provider's billed spend over the last day rose sharply against its own 7-day baseline.
   *
   * ## One row per provider; the rung is the occurrence severity
   *
   * PLATFORM-scoped and keyed `(model_provider, <ModelProvider>)`: the provider account is
   * shared, so one anomaly is one row however many tenants drive it. The ladder rung
   * (NOTICE / ELEVATED / URGENT / CRITICAL) is reported as the occurrence severity (low /
   * medium / high / critical), so a climb ESCALATES the row and pages once per rung, while a
   * fall back down the ladder only moves the count: severity is a high-water mark for as long
   * as the row stays open. That is the hysteresis the old Redis rung claims implemented by hand.
   * A provider that scores nothing on an evaluation resolves the row.
   *
   * ## Pages
   *
   * It exists to warn BEFORE an account stops working — the 2026-08-05 Anthropic credit
   * exhaustion is the outage it is built to make impossible to reach silently. A warning
   * nobody hears until they open a page arrives too late to be one.
   */
  PROVIDER_SPEND_BURN_ANOMALY: {
    category: ISSUE_CATEGORY.BILLING,
    message:
      "A model provider's billed spend rose sharply against its own recent baseline — a burn anomaly, not a runway forecast",
    severity: "medium",
    page: true,
    // NOTICE (low) is recorded and silent: the rung says "watch this", not "act now". ELEVATED
    // (medium) and above page, and a climb from NOTICE pages as it crosses into ELEVATED.
    pageFrom: "medium",
    remedy:
      "Read `rawPayload`: `recentUsdPerDay` against `baselineUsdPerDay` and their `ratio`, and `topOrganization` with its `sharePercent` of the recent burn. " +
      "A share near 100% means one tenant IS the rise — look at that organization's workflows and agents before anything platform-wide. A small share means the rise is spread across the fleet. " +
      "This is a BURN ANOMALY, not days of credit left: no provider publishes an account balance to any credential this platform holds, and a flat burn can still empty an account without tripping it — check the provider console's balance yourself. " +
      "Figures are BILLED spend (retail, platform markup included), so read them as a trend, not as the provider's invoice. " +
      "Thresholds are in apps/backend/src/admin/metrics/domain/services/provider-burn-forecast.service.ts. The issue resolves itself on the first evaluation that scores the provider below the ladder."
  }
} as const satisfies Record<string, IssueDefinition>;
